import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../utils/client";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { Link, useParams } from "react-router-dom";
import SponsorCarousel from "../components/SponsorCarousel";
import BlogComments from "../components/BlogComments";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState([]);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [reload, setReload] = useState(false);
  const { postId } = useParams();

  useEffect(() => {
    setIsLoadingPage(true);
    if (postId) {
      client
        .get(`/blog/${postId}`)
        .then((res) => setPosts([res.data]))
        .finally(() => setIsLoadingPage(false));
    } else {
      client
        .get(`/blog?page=${currentPage}`)
        .then((res) => {
          setPosts(res.data.postItems);
          setCurrentPage(res.data.currentPage);
          setTotalPages(
            Array.from({ length: res.data.totalPages }, (_, i) => i + 1)
          );
        })
        .finally(() => setIsLoadingPage(false));
    }
  }, [currentPage, postId, reload]);

  return (
    <Layout>
      <Box maxW="1400px" mx="auto" px={[4, 6, 8]} py={[6, 10]}>
        <Flex alignItems="flex-start" flexDir={["column", "column", "row"]} gap={8}>
          {/* Main content */}
          <Box flex={1} minW={0}>
            <Skeleton isLoaded={!isLoadingPage}>
              <Heading
                fontSize={["1.6rem", "2rem"]}
                color="green.700"
                mb={1}
                borderLeft="4px solid"
                borderColor="green.400"
                pl={4}
              >
                Blog
              </Heading>
              <Box w="60px" h="3px" bg="green.400" ml={4} mb={8} borderRadius="2px" />

              <Flex flexDir="column" gap={8}>
                {posts.length === 0 && !isLoadingPage && (
                  <Box
                    bg="white"
                    borderRadius="1.25rem"
                    p={10}
                    textAlign="center"
                    border="1px solid"
                    borderColor="gray.100"
                  >
                    <Text color="gray.400" fontSize="1.1rem">
                      Nenhuma publicação ainda.
                    </Text>
                  </Box>
                )}

                {posts.map((post) => (
                  <Box
                    key={post.postId}
                    bg="white"
                    borderRadius="1.25rem"
                    boxShadow="0 2px 12px rgba(0,0,0,0.07)"
                    border="1px solid"
                    borderColor="gray.100"
                    overflow="hidden"
                    p={[4, 6]}
                  >
                    {postId ? (
                      <Heading fontSize={["1.3rem", "1.6rem"]} color="gray.800" mb={2}>
                        {post.title}
                      </Heading>
                    ) : (
                      <Link to={`/blog/${post.postId}`}>
                        <Heading
                          fontSize={["1.3rem", "1.6rem"]}
                          color="gray.800"
                          _hover={{ color: "green.600" }}
                          transition="color 0.2s"
                          mb={2}
                        >
                          {post.title}
                        </Heading>
                      </Link>
                    )}

                    <Text fontSize="0.82rem" color="gray.400" mb={4}>
                      {post.authorName} &mdash;{" "}
                      {format(new Date(post.createdAt), "dd/MM/yyyy")}
                    </Text>

                    <Text
                      dangerouslySetInnerHTML={{ __html: post.text }}
                      color="gray.700"
                      lineHeight={1.8}
                      fontSize="1rem"
                    />

                    {post.images && post.images.length > 0 && (
                      <Flex gap={3} wrap="wrap" py={5}>
                        {post.images.map((img) => (
                          <Image
                            key={img}
                            borderRadius="0.75rem"
                            maxH="350px"
                            objectFit="cover"
                            boxShadow="sm"
                            src={`${process.env.REACT_APP_S3_BASEURL}/news_images/${img}`}
                          />
                        ))}
                      </Flex>
                    )}

                    <BlogComments
                      comments={post.comments || []}
                      postId={post.postId}
                      reload={reload}
                      setReload={setReload}
                      preview={!postId}
                    />
                  </Box>
                ))}
              </Flex>

              {!postId && totalPages.length > 0 && (
                <Flex gap={2} mt={8} alignItems="center" justifyContent="center">
                  {totalPages.map((page) =>
                    page === currentPage ? (
                      <Box
                        key={page}
                        w="36px"
                        h="36px"
                        borderRadius="50%"
                        bg="green.500"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight={700}
                        fontSize="0.9rem"
                      >
                        {page}
                      </Box>
                    ) : (
                      <Button
                        key={page}
                        colorScheme="green"
                        size="sm"
                        variant="outline"
                        borderRadius="50%"
                        w="36px"
                        h="36px"
                        p={0}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    )
                  )}
                </Flex>
              )}
            </Skeleton>
          </Box>

          {/* Sidebar */}
          <Flex
            flexDir="column"
            w={["100%", "100%", "260px"]}
            flexShrink={0}
            gap={6}
          >
            <SponsorCarousel type="Principal" />
            <SponsorCarousel type="Secundário" />
          </Flex>
        </Flex>
      </Box>
    </Layout>
  );
};

export default Blog;
